// src/components/avatars/AvatarSkeleton.js

import React, { forwardRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { resolveAvatarSize } from './size';
import styles from './AvatarSkeleton.module.css';

const AvatarSkeleton = forwardRef(function AvatarSkeleton(
  { as: As = 'span', size, shape = 'circle', className = '', style, ...props },
  ref
) {
  const {
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledby,
    'aria-hidden': ariaHiddenProp,
    ...restProps
  } = props;

  const sizeResolution = useMemo(() => resolveAvatarSize(size), [size]);

  const styleVars = {};
  if (sizeResolution.kind === 'custom' && sizeResolution.value) {
    styleVars['--avatar-size'] = sizeResolution.value;
  }

  const mergedStyle = style ? { ...styleVars, ...style } : styleVars;
  const hasExplicitSize = size != null;
  const dataSize = hasExplicitSize
    ? sizeResolution.kind === 'token'
      ? sizeResolution.token
      : sizeResolution.kind === 'custom'
        ? 'custom'
        : undefined
    : undefined;

  const ariaHidden =
    ariaHiddenProp !== undefined
      ? ariaHiddenProp
      : ariaLabel || ariaLabelledby
      ? undefined
      : true;

  return (
    <As
      ref={ref}
      className={`${styles.avatarSkeleton} ${className}`.trim()}
      data-avatar-skeleton=""
      data-shape={shape}
      data-size={dataSize}
      data-fluid={sizeResolution.kind === 'fluid' ? '' : undefined}
      style={mergedStyle}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledby}
      aria-hidden={ariaHidden}
      {...restProps}
    />
  );
});

AvatarSkeleton.displayName = 'AvatarSkeleton';

AvatarSkeleton.propTypes = {
  as: PropTypes.elementType,
  size: PropTypes.oneOfType([
    PropTypes.oneOf(['fluid', 'xs', 'sm', 'md', 'lg', 'xl']),
    PropTypes.number,
    PropTypes.string
  ]),
  shape: PropTypes.oneOf(['circle', 'rounded', 'squircle', 'square']),
  className: PropTypes.string,
  style: PropTypes.object
};

export default AvatarSkeleton;
